import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { 
  LayoutDashboard, Users, BookOpen, Building2, UsersRound, FileText, 
  LogOut, ChevronRight, TrendingUp, TrendingDown, Activity, Search,
  Plus, Edit, Trash2, Eye, MoreHorizontal, Settings, Bell, Shield,
  GraduationCap, UserCog, BarChart3, PieChart, Calendar, Clock,
  CheckCircle, XCircle, AlertCircle, Loader2, RefreshCw, Download,
  Mail, Phone, Globe, MapPin
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartsPie, Pie, Cell, Legend } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Color palette
const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminInfo, setAdminInfo] = useState(null);
  const [activeSection, setActiveSection] = useState('dashboard');
  
  // Data states
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [registrationTrends, setRegistrationTrends] = useState([]);
  const [courseStats, setCourseStats] = useState(null);
  
  // Filter states
  const [userFilter, setUserFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [editingUser, setEditingUser] = useState(null);
  const [editingCourse, setEditingCourse] = useState(null);
  const [editingInstitution, setEditingInstitution] = useState(null);
  const [editingTeamMember, setEditingTeamMember] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  const token = localStorage.getItem('faheem_token');
  
  // Check admin status on load
  useEffect(() => {
    checkAdminStatus();
  }, []);
  
  const checkAdminStatus = async () => {
    if (!token) {
      navigate('/login');
      return;
    }
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/check`, {
        params: { access_token: token }
      });
      
      if (response.data.is_admin) {
        setIsAdmin(true);
        setAdminInfo(response.data);
        loadDashboardData();
      } else {
        toast.error('Admin access required');
        navigate('/');
      }
    } catch (error) {
      toast.error('Failed to verify admin status');
      navigate('/login');
    }
  };
  
  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      // Load all data in parallel
      const [statsRes, trendsRes, courseStatsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/stats/overview`, { params: { access_token: token } }),
        axios.get(`${BACKEND_URL}/api/admin/stats/registrations`, { params: { access_token: token, days: 14 } }),
        axios.get(`${BACKEND_URL}/api/admin/stats/courses`, { params: { access_token: token } })
      ]);
      
      setStats(statsRes.data);
      setRegistrationTrends(trendsRes.data.trends);
      setCourseStats(courseStatsRes.data);
      
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };
  
  const loadUsers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/users`, {
        params: { access_token: token, role: userFilter === 'all' ? undefined : userFilter }
      });
      setUsers(response.data.users);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };
  
  const loadCourses = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/courses`, {
        params: { access_token: token }
      });
      setCourses(response.data.courses);
    } catch (error) {
      toast.error('Failed to load courses');
    }
  };
  
  const loadInstitutions = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/institutions`, {
        params: { access_token: token }
      });
      setInstitutions(response.data.institutions);
    } catch (error) {
      toast.error('Failed to load institutions');
    }
  };
  
  const loadTeamMembers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/team`, {
        params: { access_token: token }
      });
      setTeamMembers(response.data.team);
    } catch (error) {
      toast.error('Failed to load team members');
    }
  };
  
  const loadSubmissions = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/submissions`, {
        params: { access_token: token }
      });
      setSubmissions(response.data.submissions);
    } catch (error) {
      toast.error('Failed to load submissions');
    }
  };
  
  // Load data when section changes
  useEffect(() => {
    if (!isAdmin) return;
    
    switch (activeSection) {
      case 'users':
        loadUsers();
        break;
      case 'courses':
        loadCourses();
        loadInstitutions(); // Need for dropdown
        break;
      case 'institutions':
        loadInstitutions();
        break;
      case 'team':
        loadTeamMembers();
        break;
      case 'submissions':
        loadSubmissions();
        break;
      default:
        break;
    }
  }, [activeSection, isAdmin, userFilter]);
  
  // CRUD Operations
  const handleUpdateUser = async (userId, userData) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/users/${userId}`, userData, {
        params: { access_token: token }
      });
      toast.success('User updated successfully');
      loadUsers();
      setEditingUser(null);
      setIsDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };
  
  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/users/${userId}`, {
        params: { access_token: token }
      });
      toast.success('User deleted successfully');
      loadUsers();
      setDeleteConfirm(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };
  
  const handleSaveCourse = async (courseData) => {
    try {
      if (editingCourse?.id && !editingCourse.isNew) {
        await axios.put(`${BACKEND_URL}/api/admin/courses/${editingCourse.id}`, courseData, {
          params: { access_token: token }
        });
        toast.success('Course updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/courses`, courseData, {
          params: { access_token: token }
        });
        toast.success('Course created successfully');
      }
      loadCourses();
      setEditingCourse(null);
      setIsDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save course');
    }
  };
  
  const handleDeleteCourse = async (courseId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/courses/${courseId}`, {
        params: { access_token: token }
      });
      toast.success('Course deleted successfully');
      loadCourses();
      setDeleteConfirm(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete course');
    }
  };
  
  const handleSaveInstitution = async (instData) => {
    try {
      if (editingInstitution?.id && !editingInstitution.isNew) {
        await axios.put(`${BACKEND_URL}/api/admin/institutions/${editingInstitution.id}`, instData, {
          params: { access_token: token }
        });
        toast.success('Institution updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/institutions`, instData, {
          params: { access_token: token }
        });
        toast.success('Institution created successfully');
      }
      loadInstitutions();
      setEditingInstitution(null);
      setIsDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save institution');
    }
  };
  
  const handleDeleteInstitution = async (instId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/institutions/${instId}`, {
        params: { access_token: token }
      });
      toast.success('Institution deleted successfully');
      loadInstitutions();
      setDeleteConfirm(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete institution');
    }
  };
  
  const handleSaveTeamMember = async (memberData) => {
    try {
      if (editingTeamMember?.id && !editingTeamMember.isNew) {
        await axios.put(`${BACKEND_URL}/api/admin/team/${editingTeamMember.id}`, memberData, {
          params: { access_token: token }
        });
        toast.success('Team member updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/team`, memberData, {
          params: { access_token: token }
        });
        toast.success('Team member created successfully');
      }
      loadTeamMembers();
      setEditingTeamMember(null);
      setIsDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save team member');
    }
  };
  
  const handleDeleteTeamMember = async (memberId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/team/${memberId}`, {
        params: { access_token: token }
      });
      toast.success('Team member deleted successfully');
      loadTeamMembers();
      setDeleteConfirm(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete team member');
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem('faheem_token');
    localStorage.removeItem('faheem_auth');
    localStorage.removeItem('faheem_user');
    localStorage.removeItem('faheem_role');
    window.dispatchEvent(new Event('authChange'));
    navigate('/');
  };
  
  // Sidebar navigation items
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'courses', label: 'Courses', icon: BookOpen },
    { id: 'institutions', label: 'Institutions', icon: Building2 },
    { id: 'team', label: 'Team Members', icon: UsersRound },
    { id: 'submissions', label: 'Submissions', icon: FileText },
  ];
  
  if (isLoading && !isAdmin) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-slate-400">Verifying admin access...</p>
        </div>
      </div>
    );
  }
  
  if (!isAdmin) {
    return null;
  }
  
  // Prepare chart data
  const userDistributionData = stats ? [
    { name: 'Students', value: stats.users.students, color: '#3B82F6' },
    { name: 'Teachers', value: stats.users.teachers, color: '#10B981' },
    { name: 'Admins', value: stats.users.admins, color: '#F59E0B' }
  ] : [];
  
  return (
    <div className="min-h-screen bg-slate-900 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-800 border-r border-slate-700 flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-slate-700">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-white">FaheemGPT</h1>
              <p className="text-xs text-slate-400">Admin Panel</p>
            </div>
          </Link>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all ${
                activeSection === item.id
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-400 hover:bg-slate-700 hover:text-white'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </button>
          ))}
        </nav>
        
        {/* User Info & Logout */}
        <div className="p-4 border-t border-slate-700">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <span className="text-white font-bold text-sm">
                {adminInfo?.username?.charAt(0)?.toUpperCase() || 'A'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{adminInfo?.username || 'Admin'}</p>
              <p className="text-xs text-slate-400 truncate">{adminInfo?.email}</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Header */}
        <header className="bg-slate-800 border-b border-slate-700 px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white capitalize">
                {activeSection === 'dashboard' ? 'Dashboard Overview' : activeSection}
              </h2>
              <p className="text-slate-400 text-sm">
                {activeSection === 'dashboard' 
                  ? 'Monitor your platform performance and key metrics'
                  : `Manage ${activeSection} on your platform`}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Button 
                variant="outline" 
                size="sm"
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
                onClick={loadDashboardData}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </header>
        
        {/* Content Area */}
        <div className="p-8">
          {/* Dashboard Section */}
          {activeSection === 'dashboard' && (
            <div className="space-y-8">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatsCard
                  title="Total Users"
                  value={stats?.users?.total || 0}
                  icon={Users}
                  trend="+12%"
                  trendUp={true}
                  color="blue"
                  subtitle={`${stats?.users?.students || 0} students, ${stats?.users?.teachers || 0} teachers`}
                />
                <StatsCard
                  title="Active Courses"
                  value={stats?.courses?.active || 0}
                  icon={BookOpen}
                  trend="+5%"
                  trendUp={true}
                  color="green"
                  subtitle={`${stats?.courses?.total || 0} total courses`}
                />
                <StatsCard
                  title="Total Enrollments"
                  value={stats?.enrollments?.total || 0}
                  icon={GraduationCap}
                  trend="+18%"
                  trendUp={true}
                  color="purple"
                  subtitle="Across all courses"
                />
                <StatsCard
                  title="Pending Grades"
                  value={stats?.assignments?.pending || 0}
                  icon={FileText}
                  trend={stats?.assignments?.pending > 0 ? "Needs attention" : "All clear"}
                  trendUp={stats?.assignments?.pending === 0}
                  color="amber"
                  subtitle={`${stats?.assignments?.graded || 0} graded`}
                />
              </div>
              
              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Registration Trends */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-blue-500" />
                      Registration Trends (Last 14 Days)
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                      New user registrations over time
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={registrationTrends}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis 
                            dataKey="date" 
                            stroke="#9CA3AF"
                            tickFormatter={(value) => new Date(value).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                          />
                          <YAxis stroke="#9CA3AF" />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px' }}
                            labelStyle={{ color: '#F3F4F6' }}
                          />
                          <Bar dataKey="students" fill="#3B82F6" name="Students" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="teachers" fill="#10B981" name="Teachers" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
                
                {/* User Distribution */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <PieChart className="w-5 h-5 text-purple-500" />
                      User Distribution
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                      Breakdown by role
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPie>
                          <Pie
                            data={userDistributionData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            {userDistributionData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px' }}
                          />
                          <Legend 
                            formatter={(value) => <span style={{ color: '#F3F4F6' }}>{value}</span>}
                          />
                        </RechartsPie>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Popular Courses */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-500" />
                    Most Popular Courses
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Top courses by enrollment
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {courseStats?.most_popular?.map((course, index) => (
                      <div key={course.id} className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            index === 0 ? 'bg-yellow-500/20 text-yellow-500' :
                            index === 1 ? 'bg-slate-400/20 text-slate-400' :
                            index === 2 ? 'bg-amber-600/20 text-amber-600' :
                            'bg-slate-600/20 text-slate-500'
                          }`}>
                            <span className="font-bold">#{index + 1}</span>
                          </div>
                          <div>
                            <p className="font-medium text-white">{course.title}</p>
                            <p className="text-sm text-slate-400">{course.institution || 'No institution'}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-white">{course.enrollments}</p>
                          <p className="text-sm text-slate-400">enrollments</p>
                        </div>
                      </div>
                    ))}
                    {(!courseStats?.most_popular || courseStats.most_popular.length === 0) && (
                      <p className="text-slate-400 text-center py-8">No course data available</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* Users Section */}
          {activeSection === 'users' && (
            <div className="space-y-6">
              {/* Filters */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search users..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 bg-slate-800 border-slate-700 text-white w-64"
                    />
                  </div>
                  <Select value={userFilter} onValueChange={setUserFilter}>
                    <SelectTrigger className="w-40 bg-slate-800 border-slate-700 text-white">
                      <SelectValue placeholder="Filter by role" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="all">All Roles</SelectItem>
                      <SelectItem value="student">Students</SelectItem>
                      <SelectItem value="teacher">Teachers</SelectItem>
                      <SelectItem value="admin">Admins</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Users Table */}
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-700/50">
                        <tr>
                          <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">User</th>
                          <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Email</th>
                          <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Role</th>
                          <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Enrolled</th>
                          <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Joined</th>
                          <th className="text-right py-4 px-6 text-sm font-medium text-slate-300">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700">
                        {users
                          .filter(user => 
                            searchQuery === '' || 
                            user.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            user.email?.toLowerCase().includes(searchQuery.toLowerCase())
                          )
                          .map((user) => (
                          <tr key={user.id} className="hover:bg-slate-700/30">
                            <td className="py-4 px-6">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                                  {user.avatar_url ? (
                                    <img src={user.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                                  ) : (
                                    <span className="text-white font-bold text-sm">
                                      {user.username?.charAt(0)?.toUpperCase() || 'U'}
                                    </span>
                                  )}
                                </div>
                                <span className="font-medium text-white">{user.username || 'Unknown'}</span>
                              </div>
                            </td>
                            <td className="py-4 px-6 text-slate-300">{user.email}</td>
                            <td className="py-4 px-6">
                              <Badge className={`${
                                user.role === 'admin' ? 'bg-amber-500/20 text-amber-500' :
                                user.role === 'teacher' ? 'bg-green-500/20 text-green-500' :
                                'bg-blue-500/20 text-blue-500'
                              }`}>
                                {user.role || 'student'}
                              </Badge>
                            </td>
                            <td className="py-4 px-6 text-slate-300">
                              {user.enrolled_courses?.length || 0} courses
                            </td>
                            <td className="py-4 px-6 text-slate-400 text-sm">
                              {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                            </td>
                            <td className="py-4 px-6">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-slate-400 hover:text-white"
                                  onClick={() => {
                                    setEditingUser(user);
                                    setIsDialogOpen(true);
                                  }}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-400 hover:text-red-300"
                                  onClick={() => setDeleteConfirm({ type: 'user', id: user.id, name: user.username })}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* Courses Section */}
          {activeSection === 'courses' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search courses..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-700 text-white w-64"
                  />
                </div>
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => {
                    setEditingCourse({ isNew: true, id: '', title: '', description: '', status: 'active', type: 'academic', level: 'Beginner' });
                    setIsDialogOpen(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Course
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {courses
                  .filter(course => 
                    searchQuery === '' || 
                    course.title?.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .map((course) => (
                  <Card key={course.id} className="bg-slate-800 border-slate-700">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${course.color || 'blue'}-500/20`}>
                          <BookOpen className={`w-6 h-6 text-${course.color || 'blue'}-500`} />
                        </div>
                        <Badge className={course.status === 'active' ? 'bg-green-500/20 text-green-500' : 'bg-slate-500/20 text-slate-400'}>
                          {course.status || 'active'}
                        </Badge>
                      </div>
                      <CardTitle className="text-white mt-4">{course.title}</CardTitle>
                      <CardDescription className="text-slate-400 line-clamp-2">
                        {course.description || 'No description'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between text-slate-400">
                          <span>Level</span>
                          <span className="text-white">{course.level || 'Beginner'}</span>
                        </div>
                        <div className="flex justify-between text-slate-400">
                          <span>Type</span>
                          <span className="text-white capitalize">{course.type || 'academic'}</span>
                        </div>
                        <div className="flex justify-between text-slate-400">
                          <span>Institution</span>
                          <span className="text-white">{course.institution_display || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-700">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                          onClick={() => {
                            setEditingCourse(course);
                            setIsDialogOpen(true);
                          }}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          onClick={() => setDeleteConfirm({ type: 'course', id: course.id, name: course.title })}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
          
          {/* Institutions Section */}
          {activeSection === 'institutions' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search institutions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-700 text-white w-64"
                  />
                </div>
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => {
                    setEditingInstitution({ isNew: true, id: '', name: '', country: '', status: 'active' });
                    setIsDialogOpen(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Institution
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {institutions
                  .filter(inst => 
                    searchQuery === '' || 
                    inst.name?.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .map((inst) => (
                  <Card key={inst.id} className="bg-slate-800 border-slate-700">
                    <CardHeader>
                      <div className="flex items-center gap-4">
                        {inst.logo_url ? (
                          <img src={inst.logo_url} alt="" className="w-16 h-16 rounded-lg object-contain bg-white p-2" />
                        ) : (
                          <div className="w-16 h-16 rounded-lg bg-slate-700 flex items-center justify-center">
                            <Building2 className="w-8 h-8 text-slate-400" />
                          </div>
                        )}
                        <div>
                          <CardTitle className="text-white">{inst.name}</CardTitle>
                          <CardDescription className="text-slate-400 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {inst.country || 'Unknown'}
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        {inst.website_link && (
                          <div className="flex items-center gap-2 text-slate-400">
                            <Globe className="w-4 h-4" />
                            <a href={inst.website_link} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline truncate">
                              {inst.website_link}
                            </a>
                          </div>
                        )}
                        <div className="flex justify-between text-slate-400">
                          <span>Type</span>
                          <span className="text-white capitalize">{inst.institution_type || 'university'}</span>
                        </div>
                        <div className="flex justify-between text-slate-400">
                          <span>Status</span>
                          <Badge className={inst.status === 'active' ? 'bg-green-500/20 text-green-500' : 'bg-slate-500/20 text-slate-400'}>
                            {inst.status || 'active'}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-700">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                          onClick={() => {
                            setEditingInstitution(inst);
                            setIsDialogOpen(true);
                          }}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          onClick={() => setDeleteConfirm({ type: 'institution', id: inst.id, name: inst.name })}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
          
          {/* Team Members Section */}
          {activeSection === 'team' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search team members..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-700 text-white w-64"
                  />
                </div>
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => {
                    setEditingTeamMember({ isNew: true, id: '', name: '', title: '', role: '', image_url: '', department: '', order: 0 });
                    setIsDialogOpen(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Team Member
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {teamMembers
                  .filter(member => 
                    searchQuery === '' || 
                    member.name?.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .map((member) => (
                  <Card key={member.id} className="bg-slate-800 border-slate-700">
                    <CardContent className="pt-6 text-center">
                      <div className="w-24 h-24 rounded-full mx-auto mb-4 overflow-hidden bg-slate-700">
                        {member.image_url ? (
                          <img src={member.image_url} alt={member.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Users className="w-10 h-10 text-slate-400" />
                          </div>
                        )}
                      </div>
                      <h3 className="font-semibold text-white">{member.name}</h3>
                      <p className="text-sm text-blue-400">{member.title}</p>
                      <p className="text-xs text-slate-400 mt-1">{member.department}</p>
                      <div className="flex items-center justify-center gap-2 mt-4">
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-slate-600 text-slate-300 hover:bg-slate-700"
                          onClick={() => {
                            setEditingTeamMember(member);
                            setIsDialogOpen(true);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          onClick={() => setDeleteConfirm({ type: 'team', id: member.id, name: member.name })}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
          
          {/* Submissions Section */}
          {activeSection === 'submissions' && (
            <div className="space-y-6">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Assignment Submissions</CardTitle>
                  <CardDescription className="text-slate-400">
                    All student assignment submissions across courses
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-700/50">
                        <tr>
                          <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Student</th>
                          <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Course</th>
                          <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">File</th>
                          <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Status</th>
                          <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Grade</th>
                          <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Submitted</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700">
                        {submissions.map((sub) => (
                          <tr key={sub.id} className="hover:bg-slate-700/30">
                            <td className="py-4 px-6">
                              <div>
                                <p className="font-medium text-white">{sub.student_name}</p>
                                <p className="text-sm text-slate-400">{sub.student_email}</p>
                              </div>
                            </td>
                            <td className="py-4 px-6 text-slate-300">{sub.course_title}</td>
                            <td className="py-4 px-6">
                              {sub.file_url ? (
                                <a href={sub.file_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline text-sm">
                                  View File
                                </a>
                              ) : (
                                <span className="text-slate-500">No file</span>
                              )}
                            </td>
                            <td className="py-4 px-6">
                              <Badge className={sub.is_graded ? 'bg-green-500/20 text-green-500' : 'bg-amber-500/20 text-amber-500'}>
                                {sub.is_graded ? 'Graded' : 'Pending'}
                              </Badge>
                            </td>
                            <td className="py-4 px-6 text-white">
                              {sub.grade !== null ? `${sub.grade}/100` : '-'}
                            </td>
                            <td className="py-4 px-6 text-slate-400 text-sm">
                              {sub.created_at ? new Date(sub.created_at).toLocaleDateString() : 'N/A'}
                            </td>
                          </tr>
                        ))}
                        {submissions.length === 0 && (
                          <tr>
                            <td colSpan="6" className="py-12 text-center text-slate-400">
                              No submissions found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>
      
      {/* Edit User Dialog */}
      <Dialog open={isDialogOpen && editingUser !== null} onOpenChange={(open) => { if (!open) { setEditingUser(null); setIsDialogOpen(false); } }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription className="text-slate-400">
              Update user profile information
            </DialogDescription>
          </DialogHeader>
          {editingUser && (
            <UserEditForm 
              user={editingUser} 
              onSave={(data) => handleUpdateUser(editingUser.id, data)} 
              onCancel={() => { setEditingUser(null); setIsDialogOpen(false); }}
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Edit Course Dialog */}
      <Dialog open={isDialogOpen && editingCourse !== null} onOpenChange={(open) => { if (!open) { setEditingCourse(null); setIsDialogOpen(false); } }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingCourse?.isNew ? 'Add New Course' : 'Edit Course'}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingCourse?.isNew ? 'Create a new course' : 'Update course information'}
            </DialogDescription>
          </DialogHeader>
          {editingCourse && (
            <CourseEditForm 
              course={editingCourse} 
              institutions={institutions}
              onSave={handleSaveCourse} 
              onCancel={() => { setEditingCourse(null); setIsDialogOpen(false); }}
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Edit Institution Dialog */}
      <Dialog open={isDialogOpen && editingInstitution !== null} onOpenChange={(open) => { if (!open) { setEditingInstitution(null); setIsDialogOpen(false); } }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingInstitution?.isNew ? 'Add New Institution' : 'Edit Institution'}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingInstitution?.isNew ? 'Create a new institution' : 'Update institution information'}
            </DialogDescription>
          </DialogHeader>
          {editingInstitution && (
            <InstitutionEditForm 
              institution={editingInstitution} 
              onSave={handleSaveInstitution} 
              onCancel={() => { setEditingInstitution(null); setIsDialogOpen(false); }}
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Edit Team Member Dialog */}
      <Dialog open={isDialogOpen && editingTeamMember !== null} onOpenChange={(open) => { if (!open) { setEditingTeamMember(null); setIsDialogOpen(false); } }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingTeamMember?.isNew ? 'Add New Team Member' : 'Edit Team Member'}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingTeamMember?.isNew ? 'Add a new team member to display on homepage' : 'Update team member information'}
            </DialogDescription>
          </DialogHeader>
          {editingTeamMember && (
            <TeamMemberEditForm 
              member={editingTeamMember} 
              onSave={handleSaveTeamMember} 
              onCancel={() => { setEditingTeamMember(null); setIsDialogOpen(false); }}
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm !== null} onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertCircle className="w-5 h-5" />
              Confirm Deletion
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Are you sure you want to delete <span className="text-white font-medium">{deleteConfirm?.name}</span>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="border-slate-600" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => {
                if (deleteConfirm?.type === 'user') handleDeleteUser(deleteConfirm.id);
                else if (deleteConfirm?.type === 'course') handleDeleteCourse(deleteConfirm.id);
                else if (deleteConfirm?.type === 'institution') handleDeleteInstitution(deleteConfirm.id);
                else if (deleteConfirm?.type === 'team') handleDeleteTeamMember(deleteConfirm.id);
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Stats Card Component
function StatsCard({ title, value, icon: Icon, trend, trendUp, color, subtitle }) {
  const colorClasses = {
    blue: 'from-blue-500/20 to-blue-600/20 text-blue-500',
    green: 'from-green-500/20 to-green-600/20 text-green-500',
    purple: 'from-purple-500/20 to-purple-600/20 text-purple-500',
    amber: 'from-amber-500/20 to-amber-600/20 text-amber-500',
  };
  
  return (
    <Card className="bg-slate-800 border-slate-700">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center`}>
            <Icon className="w-6 h-6" />
          </div>
          <div className={`flex items-center gap-1 text-sm ${trendUp ? 'text-green-500' : 'text-amber-500'}`}>
            {trendUp ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            <span>{trend}</span>
          </div>
        </div>
        <div className="mt-4">
          <p className="text-3xl font-bold text-white">{value.toLocaleString()}</p>
          <p className="text-slate-400 font-medium">{title}</p>
          {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

// Edit Forms
function UserEditForm({ user, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    username: user.username || '',
    email: user.email || '',
    role: user.role || 'student',
    bio: user.bio || '',
    avatar_url: user.avatar_url || ''
  });
  
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-slate-300">Username</Label>
          <Input 
            value={formData.username} 
            onChange={(e) => setFormData({...formData, username: e.target.value})}
            className="bg-slate-700 border-slate-600 text-white mt-1"
          />
        </div>
        <div>
          <Label className="text-slate-300">Role</Label>
          <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
            <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-700 border-slate-600">
              <SelectItem value="student">Student</SelectItem>
              <SelectItem value="teacher">Teacher</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label className="text-slate-300">Avatar URL</Label>
        <Input 
          value={formData.avatar_url} 
          onChange={(e) => setFormData({...formData, avatar_url: e.target.value})}
          className="bg-slate-700 border-slate-600 text-white mt-1"
          placeholder="https://..."
        />
      </div>
      <div>
        <Label className="text-slate-300">Bio</Label>
        <Textarea 
          value={formData.bio} 
          onChange={(e) => setFormData({...formData, bio: e.target.value})}
          className="bg-slate-700 border-slate-600 text-white mt-1"
          rows={3}
        />
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" className="border-slate-600" onClick={onCancel}>Cancel</Button>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => onSave(formData)}>Save Changes</Button>
      </div>
    </div>
  );
}

function CourseEditForm({ course, institutions, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    id: course.id || '',
    title: course.title || '',
    description: course.description || '',
    icon: course.icon || 'BookOpen',
    color: course.color || 'blue',
    level: course.level || 'Beginner',
    modules: course.modules || 0,
    status: course.status || 'active',
    instructor: course.instructor || '',
    institution_id: course.institution_id || '',
    type: course.type || 'academic'
  });
  
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-slate-300">Course ID</Label>
          <Input 
            value={formData.id} 
            onChange={(e) => setFormData({...formData, id: e.target.value})}
            className="bg-slate-700 border-slate-600 text-white mt-1"
            disabled={!course.isNew}
            placeholder="unique-course-id"
          />
        </div>
        <div>
          <Label className="text-slate-300">Title</Label>
          <Input 
            value={formData.title} 
            onChange={(e) => setFormData({...formData, title: e.target.value})}
            className="bg-slate-700 border-slate-600 text-white mt-1"
          />
        </div>
      </div>
      <div>
        <Label className="text-slate-300">Description</Label>
        <Textarea 
          value={formData.description} 
          onChange={(e) => setFormData({...formData, description: e.target.value})}
          className="bg-slate-700 border-slate-600 text-white mt-1"
          rows={3}
        />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label className="text-slate-300">Level</Label>
          <Select value={formData.level} onValueChange={(value) => setFormData({...formData, level: value})}>
            <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-700 border-slate-600">
              <SelectItem value="Beginner">Beginner</SelectItem>
              <SelectItem value="Intermediate">Intermediate</SelectItem>
              <SelectItem value="Advanced">Advanced</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-slate-300">Type</Label>
          <Select value={formData.type} onValueChange={(value) => setFormData({...formData, type: value})}>
            <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-700 border-slate-600">
              <SelectItem value="academic">Academic</SelectItem>
              <SelectItem value="life-skills">Life-Skills</SelectItem>
              <SelectItem value="services">Services</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-slate-300">Status</Label>
          <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
            <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-700 border-slate-600">
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-slate-300">Institution</Label>
          <Select value={formData.institution_id || "none"} onValueChange={(value) => setFormData({...formData, institution_id: value === "none" ? "" : value})}>
            <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1">
              <SelectValue placeholder="Select institution" />
            </SelectTrigger>
            <SelectContent className="bg-slate-700 border-slate-600">
              <SelectItem value="none">None</SelectItem>
              {institutions.map((inst) => (
                <SelectItem key={inst.id} value={inst.id}>{inst.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-slate-300">Instructor ID/Email</Label>
          <Input 
            value={formData.instructor} 
            onChange={(e) => setFormData({...formData, instructor: e.target.value})}
            className="bg-slate-700 border-slate-600 text-white mt-1"
            placeholder="user-id or email"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" className="border-slate-600" onClick={onCancel}>Cancel</Button>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => onSave(formData)}>
          {course.isNew ? 'Create Course' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}

function InstitutionEditForm({ institution, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    id: institution.id || '',
    name: institution.name || '',
    country: institution.country || '',
    logo_url: institution.logo_url || '',
    website_link: institution.website_link || '',
    institution_type: institution.institution_type || 'university',
    status: institution.status || 'active'
  });
  
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-slate-300">Institution ID</Label>
          <Input 
            value={formData.id} 
            onChange={(e) => setFormData({...formData, id: e.target.value})}
            className="bg-slate-700 border-slate-600 text-white mt-1"
            disabled={!institution.isNew}
            placeholder="unique-institution-id"
          />
        </div>
        <div>
          <Label className="text-slate-300">Name</Label>
          <Input 
            value={formData.name} 
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            className="bg-slate-700 border-slate-600 text-white mt-1"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-slate-300">Country</Label>
          <Input 
            value={formData.country} 
            onChange={(e) => setFormData({...formData, country: e.target.value})}
            className="bg-slate-700 border-slate-600 text-white mt-1"
          />
        </div>
        <div>
          <Label className="text-slate-300">Type</Label>
          <Select value={formData.institution_type} onValueChange={(value) => setFormData({...formData, institution_type: value})}>
            <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-700 border-slate-600">
              <SelectItem value="university">University</SelectItem>
              <SelectItem value="college">College</SelectItem>
              <SelectItem value="school">School</SelectItem>
              <SelectItem value="institute">Institute</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label className="text-slate-300">Logo URL</Label>
        <Input 
          value={formData.logo_url} 
          onChange={(e) => setFormData({...formData, logo_url: e.target.value})}
          className="bg-slate-700 border-slate-600 text-white mt-1"
          placeholder="https://..."
        />
      </div>
      <div>
        <Label className="text-slate-300">Website</Label>
        <Input 
          value={formData.website_link} 
          onChange={(e) => setFormData({...formData, website_link: e.target.value})}
          className="bg-slate-700 border-slate-600 text-white mt-1"
          placeholder="https://..."
        />
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" className="border-slate-600" onClick={onCancel}>Cancel</Button>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => onSave(formData)}>
          {institution.isNew ? 'Create Institution' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}

function TeamMemberEditForm({ member, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    id: member.id || '',
    name: member.name || '',
    title: member.title || '',
    role: member.role || '',
    image_url: member.image_url || '',
    department: member.department || '',
    color: member.color || 'blue',
    flags: member.flags || [],
    order: member.order || 0
  });
  
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-slate-300">Member ID</Label>
          <Input 
            value={formData.id} 
            onChange={(e) => setFormData({...formData, id: e.target.value})}
            className="bg-slate-700 border-slate-600 text-white mt-1"
            disabled={!member.isNew}
            placeholder="unique-member-id"
          />
        </div>
        <div>
          <Label className="text-slate-300">Full Name</Label>
          <Input 
            value={formData.name} 
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            className="bg-slate-700 border-slate-600 text-white mt-1"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-slate-300">Title</Label>
          <Input 
            value={formData.title} 
            onChange={(e) => setFormData({...formData, title: e.target.value})}
            className="bg-slate-700 border-slate-600 text-white mt-1"
            placeholder="e.g. CEO, Lead Developer"
          />
        </div>
        <div>
          <Label className="text-slate-300">Role</Label>
          <Input 
            value={formData.role} 
            onChange={(e) => setFormData({...formData, role: e.target.value})}
            className="bg-slate-700 border-slate-600 text-white mt-1"
            placeholder="e.g. Founder, Engineer"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-slate-300">Department</Label>
          <Input 
            value={formData.department} 
            onChange={(e) => setFormData({...formData, department: e.target.value})}
            className="bg-slate-700 border-slate-600 text-white mt-1"
            placeholder="e.g. Engineering, Design"
          />
        </div>
        <div>
          <Label className="text-slate-300">Display Order</Label>
          <Input 
            type="number"
            value={formData.order} 
            onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
            className="bg-slate-700 border-slate-600 text-white mt-1"
          />
        </div>
      </div>
      <div>
        <Label className="text-slate-300">Image URL</Label>
        <Input 
          value={formData.image_url} 
          onChange={(e) => setFormData({...formData, image_url: e.target.value})}
          className="bg-slate-700 border-slate-600 text-white mt-1"
          placeholder="https://..."
        />
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" className="border-slate-600" onClick={onCancel}>Cancel</Button>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => onSave(formData)}>
          {member.isNew ? 'Add Team Member' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}
